<script setup lang="ts">
import TypeaheadPicker from '@/components/TypeaheadPicker.vue'
import { keepingBetaOptIn } from '@/util/beta_opt_in'
import { toolkitUrls } from '@/util/links'
import RemovableList from './RemovableList.vue'
import { getStrategyOptions, type StrategyOption } from './access_points_api'
import { usePickedUuids } from './use_picked_uuids'
import { usePickerOptions } from './use_picker_options'

export interface SelectedStrategy {
  title: string
  slug: string
}

const props = defineProps<{
  id: string
  selected: string[]
  strategiesMap: Record<string, SelectedStrategy>
}>()

const emit = defineEmits<{
  'update:selected': [uuids: string[]]
  add: [uuid: string]
  'load-error': [title: string, error: unknown]
}>()

const { options, isLoading } = usePickerOptions(getStrategyOptions, error =>
  emit('load-error', 'Error fetching strategies for autocompletion', error)
)

const { add, remove } = usePickedUuids(
  () => props.selected,
  uuids => emit('update:selected', uuids),
  uuid => emit('add', uuid),
  'strategy'
)

const labelFor = (uuid: string) => props.strategiesMap[uuid]?.title || uuid
</script>

<template>
  <div class="mb-4">
    <label :for="id" class="block mb-1 font-bold">Add strategies</label>
    <TypeaheadPicker
      :id="id"
      :items="options"
      :item-label="strategy => strategy.title"
      :item-key="strategy => strategy.uuid"
      :disabled="isLoading"
      placeholder="Strategy name"
      @select="(strategy: StrategyOption) => add(strategy.uuid, strategy.title)"
    />
    <RemovableList
      :items="selected"
      :item-key="uuid => uuid"
      :item-label="labelFor"
      noun="strategy"
      :empty-focus-id="id"
      @remove="remove"
    >
      <template #item="{ item: uuid }">
        <a
          v-if="strategiesMap[uuid]"
          :href="keepingBetaOptIn(toolkitUrls.strategyPage(strategiesMap[uuid].slug))"
          target="_blank"
          class="text-primary-400 underline"
        >{{ labelFor(uuid) }}</a>
        <template v-else>{{ uuid }}</template>
      </template>
    </RemovableList>
  </div>
</template>
